<script setup lang="ts">
import { Globe } from '@lucide/vue'
import { domainSendingWaitText, memberMailDomain } from '@zerohuman/shared'
import { Button, Input, Label, MOTION, ZhCheckRow, ZhEmptyState, ZhPageTitle, ZhSectionHeading } from '@zerohuman/ui'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { api, type DomainDnsCheck, type DomainDnsRecord, type DomainMailMode, type EnterpriseDomainRow } from '@/api'

/*
 * #422 — the owner attaches their own domain. Attaching asks what happens to email there: receive it in ZeroHuman,
 * send and receive it from ZeroHuman (which needs more records), or keep email on `{slug}.zerohuman.com`. None is
 * preselected: the owner chooses. The portal and API stay on their ZeroHuman addresses either way.
 *
 * The owner adds every record at the company where they bought the domain; ZeroHuman holds no credential for it and
 * only reads what is there. What is still missing is listed on the domain until it is there.
 *
 * #524 — the page speaks plainly to a first-time owner. Every word we write around the records is plain; the records
 * table's cells stay exact, because the owner copies them into their domain company's form (the same in Standard and
 * Advanced mode). A failure shows one fixed sentence for its action, saying what did not work and what to try next,
 * and never reads the error: the server's codes and network messages are not words for the owner.
 */
const domains = ref<EnterpriseDomainRow[]>([])
const error = ref<string | null>(null)
const loading = ref(true)
const busy = ref(false)
const slugHost = ref('')
type CheckState = 'idle' | 'checking' | 'passed' | 'failed'
const rowStates = ref<Record<string, CheckState>>({})
const results = ref<Record<string, DomainDnsCheck>>({})
let checkEpoch = 0
let refresh: ReturnType<typeof setInterval> | undefined
onBeforeUnmount(() => {
    checkEpoch += 1
    if (refresh) clearInterval(refresh)
})
const recordKey = (record: DomainDnsRecord) => `${record.type}|${record.name}|${record.value}`
function clearChecks(): void {
    checkEpoch += 1
    rowStates.value = {}
    results.value = {}
}
const pause = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

const newDomain = ref('')
const newMode = ref<DomainMailMode | null>(null)
/** What the choices name before a domain is typed: the owner still sees it is theirs that each choice is about. */
const typedDomain = computed(() => newDomain.value.trim().toLowerCase() || 'your domain')
const canAttach = computed(() => newDomain.value.trim() !== '' && newMode.value !== null && !busy.value)

const STATUS_TEXT: Record<EnterpriseDomainRow['status'], string> = {
    unverified: 'Not verified: add the ownership record, then press Check records.',
    incomplete: 'Verified. Email has not moved yet: add the mail records, then press Check records.',
    verified: 'Verified. Email stays on your ZeroHuman address.',
    active: 'Verified. Email is on this domain.',
}

/** One fixed sentence per action: what did not work and what to try next. The caught error is never shown. */
const FAILED = {
    load: 'We could not load your domains, so refresh the page to try again.',
    attach: 'We could not attach that domain, so check it is written like example.com and try again.',
    check: 'We could not check your records just now, so wait a minute and press Check records again.',
    mode: 'We could not change how email works for this domain, so try again in a minute.',
    detach: 'We could not detach that domain, so try again in a minute.',
} as const

function keepHost(): string {
    return slugHost.value || 'your ZeroHuman address'
}

function mailModeText(row: EnterpriseDomainRow): string {
    if (row.mailMode === 'receive') return 'Receive email only. Sending and replies from this domain are disabled.'
    if (row.mailMode === 'keep') return `Email is set to stay on ${keepHost()}.`
    return row.sendingReady
        ? 'Sending and receiving are active.'
        : 'Sending is not active yet. Add the sending records. Verification continues automatically; you can also press Check records now.'
}

async function load(): Promise<void> {
    try {
        domains.value = await api.enterpriseDomains()
        error.value = null
    } catch {
        error.value = FAILED.load
    } finally {
        loading.value = false
    }
}

onMounted(async () => {
    refresh = setInterval(() => {
        if (!busy.value && domains.value.some(row => row.mailMode !== 'keep' && !row.mailReady)) void load()
    }, 15_000)
    const [me] = await Promise.all([api.me().catch(() => null), load()])
    slugHost.value = me ? memberMailDomain(me.enterprise.slug) : ''
})

/** Every change is answered with the domain as the API now holds it, so the list is reloaded rather than patched. */
async function act(sentence: string, change: () => Promise<unknown>): Promise<boolean> {
    clearChecks()
    busy.value = true
    error.value = null
    try {
        await change()
        await load()
        return true
    } catch {
        error.value = sentence
        return false
    } finally {
        busy.value = false
    }
}

/** The empty state's one action: the attach form is always on the page, so take the person to its Domain field. */
function focusNewDomain(): void {
    document.getElementById('new-domain')?.focus()
}

async function attach(): Promise<void> {
    const mode = newMode.value
    if (!canAttach.value || mode === null) {
        return
    }
    if (await act(FAILED.attach, () => api.attachDomain(newDomain.value.trim(), mode))) {
        newDomain.value = ''
        newMode.value = null
    }
}

async function verify(row: EnterpriseDomainRow): Promise<void> {
    if (busy.value) return
    clearChecks()
    const epoch = checkEpoch
    busy.value = true
    error.value = null
    for (const record of row.requiredRecords) rowStates.value[recordKey(record)] = 'checking'
    try {
        const checked = await api.verifyDomain(row.domain)
        if (epoch !== checkEpoch) return
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
        for (const [index, check] of checked.checks.entries()) {
            if (!reduced && index > 0) await pause(180)
            if (epoch !== checkEpoch) return
            const key = recordKey(check)
            results.value[key] = check
            rowStates.value[key] = check.published ? 'passed' : 'failed'
        }
        await pause(MOTION.decisionMs)
        if (epoch === checkEpoch) await load()
    } catch {
        if (epoch !== checkEpoch) return
        rowStates.value = {}
        error.value = FAILED.check
    } finally {
        if (epoch === checkEpoch) busy.value = false
    }
}

function switchMode(row: EnterpriseDomainRow): Promise<boolean> {
    const next: DomainMailMode = row.mailMode === 'keep' ? 'migrate' : 'keep'
    return act(FAILED.mode, () => api.setDomainMailMode(row.domain, next))
}

function enableSending(row: EnterpriseDomainRow): Promise<boolean> {
    return act(FAILED.mode, () => api.setDomainMailMode(row.domain, 'migrate'))
}

function receiveOnly(row: EnterpriseDomainRow): Promise<boolean> {
    return act(FAILED.mode, () => api.setDomainMailMode(row.domain, 'receive'))
}

async function detach(row: EnterpriseDomainRow): Promise<void> {
    const question = `Detach ${row.domain}? Email and your team's email addresses go back to how they were before it was attached.`
    if (!confirm(question)) {
        return
    }
    await act(FAILED.detach, () => api.detachDomain(row.domain))
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<ZhPageTitle :icon="Globe" title="Domains" />
		<p class="text-muted-foreground max-w-2xl text-sm">
			Attach your own domain to this enterprise. You do this by adding a few records at the company where you
			bought your domain; a record is one line in that company's settings for your domain. ZeroHuman never gets
			access to that account; it only reads the records you add. The ownership record proves the domain is yours,
			and nothing changes until it is added and checked. The mail records send the domain's email to ZeroHuman,
			and the sending records let your team send and reply from it. The portal keeps its ZeroHuman address either
			way.
		</p>

		<p v-if="error" class="text-destructive text-sm" data-testid="error">{{ error }}</p>

		<section class="space-y-3" aria-labelledby="attach-domain">
			<ZhSectionHeading id="attach-domain" title="Attach a domain" />
			<form class="max-w-2xl space-y-4" data-testid="attach-form" @submit.prevent="attach">
				<div class="space-y-2">
					<Label for="new-domain">Domain</Label>
					<Input id="new-domain" v-model="newDomain" placeholder="example.com" data-testid="new-domain" />
				</div>
				<fieldset class="space-y-2">
					<legend class="text-sm font-medium">Email</legend>
					<label class="flex items-start gap-2 text-sm">
						<input v-model="newMode" type="radio" name="mail-mode" value="receive" data-testid="mode-receive" />
						<span>
							Receive email at {{ typedDomain }}
							<span class="text-muted-foreground block">Email sent to {{ typedDomain }} arrives in ZeroHuman instead of where it arrives today, and no one can send or reply from {{ typedDomain }}.</span>
						</span>
					</label>
					<label class="flex items-start gap-2 text-sm">
						<input v-model="newMode" type="radio" name="mail-mode" value="migrate" data-testid="mode-migrate" />
						<span>
							Send and receive email at {{ typedDomain }}
							<span class="text-muted-foreground block">All email at {{ typedDomain }} moves to ZeroHuman instead of where it is today, so your team can send and reply from {{ typedDomain }}; this needs a few more records.</span>
						</span>
					</label>
					<label class="flex items-start gap-2 text-sm">
						<input v-model="newMode" type="radio" name="mail-mode" value="keep" data-testid="mode-keep" />
						<span>
							Keep email on {{ keepHost() }}
							<span class="text-muted-foreground block">Nothing happens to your email; {{ typedDomain }} is only checked to prove it is yours.</span>
						</span>
					</label>
				</fieldset>
				<Button type="submit" :disabled="!canAttach" data-testid="attach">Attach</Button>
			</form>
		</section>

		<section class="space-y-3" aria-labelledby="attached-domains">
			<ZhSectionHeading id="attached-domains" title="Attached domains" />
			<p v-if="loading" class="text-muted-foreground text-sm">Loading…</p>
			<!-- "None attached" and "we could not ask" are different answers: a failed load says so above. -->
			<ZhEmptyState v-else-if="error === null && domains.length === 0" data-testid="no-domains">
				No domain is attached yet. Domains you attach appear here, with the records to add and how their email
				is handled.
				<template #action>
					<Button type="button" size="sm" @click="focusNewDomain">Attach a domain</Button>
				</template>
			</ZhEmptyState>
			<article
				v-for="row in domains"
				:key="row.domain"
				class="border-input space-y-3 border-t pt-3"
				:data-testid="`domain-${row.domain}`"
			>
				<div class="flex flex-wrap items-center justify-between gap-2">
					<div>
						<p class="font-medium">{{ row.domain }}</p>
						<p class="text-muted-foreground text-sm" :data-testid="`status-${row.domain}`">
							{{ row.receivingReady && !row.sendingReady ? 'Receiving is active on this domain.' : STATUS_TEXT[row.status] }}
							{{ mailModeText(row) }}
						</p>
						<!-- What the sending provider still waits on, from its last check, so nobody has to guess. -->
						<div v-if="row.mailMode === 'migrate' && !row.sendingReady && row.sendingWaitingOn?.length" class="mt-1 space-y-1 text-sm" :data-testid="`sending-waits-${row.domain}`">
							<p class="font-medium">Sending waits on:</p>
							<ul class="text-muted-foreground list-disc space-y-0.5 pl-5">
								<li v-for="code in row.sendingWaitingOn" :key="code" :data-sending-wait="code">{{ domainSendingWaitText(code, row.domain) }}</li>
							</ul>
							<p v-if="row.sendingCheckedAt" class="text-muted-foreground text-xs">
								Checked {{ new Date(row.sendingCheckedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) }}; checked again every minute.
							</p>
						</div>
					</div>
					<div class="flex flex-wrap gap-2">
						<Button v-if="row.mailMode === 'receive'" size="sm" variant="outline" :disabled="busy" :data-testid="`send-${row.domain}`" @click="enableSending(row)">Send and receive email</Button>
						<Button v-if="row.mailMode !== 'receive'" size="sm" variant="outline" :disabled="busy" :data-testid="`receive-${row.domain}`" @click="receiveOnly(row)">Receive email only</Button>
						<Button
							size="sm"
							variant="outline"
							:disabled="busy"
							:data-testid="`verify-${row.domain}`"
							@click="verify(row)"
						>
							Check records
						</Button>
						<Button
							size="sm"
							variant="outline"
							:disabled="busy"
							:data-testid="`mode-${row.domain}`"
							@click="switchMode(row)"
						>
							{{ row.mailMode !== 'keep' ? `Keep email on ${keepHost()}` : `Send and receive email at ${row.domain}` }}
						</Button>
						<Button
							size="sm"
							variant="outline"
							:disabled="busy"
							:data-testid="`detach-${row.domain}`"
							@click="detach(row)"
						>
							Detach
						</Button>
					</div>
				</div>
				<table
					v-if="row.requiredRecords.length > 0"
					class="w-full text-left text-sm"
					:data-testid="`records-${row.domain}`"
				>
					<caption class="text-muted-foreground py-1 text-left text-xs">Add these records at the company where you bought your domain</caption>
					<thead class="text-muted-foreground">
						<tr>
							<th class="py-2 font-medium">Type</th>
							<th class="py-2 font-medium">Priority</th>
							<th class="py-2 font-medium">Name</th>
							<th class="py-2 font-medium">Value</th>
							<th class="py-2 font-medium">For</th>
							<th class="py-2 font-medium">Status</th>
						</tr>
					</thead>
					<tbody>
						<ZhCheckRow
							v-for="record in row.requiredRecords"
							:key="recordKey(record)"
                            :state="rowStates[recordKey(record)] ?? 'idle'"
							class="border-input border-t"
						>
							<td class="py-2 font-mono text-xs">{{ record.type }}</td>
                            <td class="py-2 font-mono text-xs">{{ record.priority ?? '—' }}</td>
							<td class="py-2 font-mono text-xs">{{ record.name }}</td>
							<td class="py-2 font-mono text-xs break-all">{{ record.value }}
                                <p v-if="results[recordKey(record)]?.published === false" class="text-muted-foreground mt-1">
                                    {{ results[recordKey(record)]?.found.length ? `Found instead: ${results[recordKey(record)]?.found.join(', ')}` : 'No record found.' }}
                                </p>
                            </td>
							<td class="py-2">{{ record.purpose === 'ownership' ? 'Ownership' : 'Email' }}</td>
						</ZhCheckRow>
					</tbody>
				</table>
			</article>
		</section>
	</section>
</template>
